/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useContext, useRef, useState, useEffect } from 'react';
import Alert from 'react-s-alert';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ReCAPTCHA from "react-google-recaptcha";

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from './ErrorBoundary';
import GeneralNavbar from './GeneralNavbar';
import FeedbackImg from '../../assets/feedback.svg';
import Footer from './Footer';


const Feedback = props => {

    const SITE_KEY = '6Lf9udAZAAAAAEFcvREE_07C1AX8HQynYoiZCZzQ';

    // contexts
    const { theme, fontFamily, fontSize, fontWeight, colors } = useContext(ThemeContext);
    const { user } = useContext(AuthContext);

    // states
    const [feedback, setFeedback] = useState({ email: user.email, name: user.username, phone: '', title: '', message: '' });
    const [isLoading, setIsLoading] = useState(false);

    //refs
    const recaptchaRef = useRef(null);

    //scroll to top
    useEffect(() => {
        window.scrollTo(0, 0);
        document.getElementById("email").focus();
    }, []);

    // handle functions
    const handleChange = e => {
        try {
            setFeedback({ ...feedback, [e.target.name]: e.target.value });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at General Feedback handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handlePhoneChange = e => {
        try {
            setFeedback({ ...feedback, phone: e });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at General Feedback handlePhoneChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const { title, email, name, phone, message, token } = feedback;

            if (token) {
                GeneralService.feedback(title, email, name, phone, message, token)
                    .then(data => {
                        // to be done - Done
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    })
                    .then(data => {
                        recaptchaRef.current.reset();
                        setFeedback({ email: '', name: '', phone: '', title: '', message: '' });
                        setIsLoading(false);
                        props.history.push('/');
                    })
            } else {
                Alert.error("Please verify captcha", {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading(false);
            }
        } catch (err) {
            setIsLoading(false);
            GeneralService.logError('at General Feedback handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    //utility function
    const onChangeCaptcha = (value) => {
        try {
            setFeedback({ ...feedback, token: value });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at General Feedback onChangeCaptcha', 'Error in try catch block', err.stack);
        }
    }

    return (
        <div className="feedbackMainDiv" style={{ fontFamily: fontFamily.primary }}>
            <ErrorBoundary errorAt="At General Feedback GeneralNavbar">
                <GeneralNavbar history={props.history} isFeedbackPage={true} />
            </ErrorBoundary>

            <div className="row p-3 px-5" style={{ background: "rgba(184, 247, 255, 0.3)" }}>
                <div className="col-md-8 col-12 mt-5">
                    <h1 id="feedback-heading" className="mb-3" style={{ fontSize: "2rem", fontWeight: "bold" }}>We want your Feedback</h1>
                    <p id="desc-para-1" style={{ fontSize: "1.2rem" }}>We strive to provide the best experience possible for our customers and that's why we need to pick your brain on how we can improve our service.<br />Please take a minute to comment on your experience with us to date.</p>
                    <p id="desc-para-2" style={{ fontSize: "1.2rem" }}>Happy reviewing!</p>
                </div>
                <img className="col-md-4 col-12" src={FeedbackImg} alt="img" />
            </div>

            <div className="mb-5 py-5 col-md-8 mt-5 offset-md-2" style={theme.form}>
                <ErrorBoundary errorAt="At General Feedback Form">
                    <form className="feedbackForm" onSubmit={handleSubmit}>
                        <h2 id="feedback-form-title" className="formTitle text-center" style={{ fontWeight: fontWeight.semiBold, color: colors.primary, fontSize: "2rem" }}>Give us your Feedback</h2>
                        <br />

                        <label htmlFor="email" style={theme.formBodyText} >Your email *</label>
                        <input type="text"
                            id="email"
                            name="email"
                            value={feedback.email}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={theme.formInput}
                        />
                        <br />

                        <label htmlFor="name" style={theme.formBodyText} >Name *</label>
                        <input type="text"
                            id="name"
                            name="name"
                            value={feedback.name}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={theme.formInput}
                        />
                        <br />

                        <label htmlFor="mobile" style={theme.formBodyText} >Mobile </label>
                        <PhoneInput
                            inputClass="form-control"
                            inputStyle={theme.formInput}
                            inputProps={{
                                id: 'phone',
                                name: 'phone'
                            }}
                            value={feedback.phone}
                            enableSearch={true}
                            onChange={handlePhoneChange} />
                        <br />

                        <label htmlFor="title" style={theme.formBodyText} >Subject *</label>
                        <input type="text"
                            id="subject"
                            name="title"
                            value={feedback.title}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={theme.formInput}
                        />
                        <br />

                        <label htmlFor="message" style={theme.formBodyText} >Message *</label>
                        <textarea
                            id="message"
                            rows="10"
                            name="message"
                            value={feedback.message}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={theme.formInput}
                        />
                        <br />

                        <ReCAPTCHA
                            id="recaptcha"
                            ref={recaptchaRef}
                            sitekey={SITE_KEY}
                            onChange={onChangeCaptcha}
                        />

                        <label id="mandatory-field-note" className="mt-1" style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }}>Fields marked with * are mandatory</label>
                        <br />

                        <button className="btn mx-auto mt-3" id="submitBtn" type="submit" style={theme.button} disabled={isLoading}>
                            {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Submit
                        </button>

                    </form>
                </ErrorBoundary>

            </div>
            <Footer />
        </div>
    )

}

export default Feedback;